import { useState, useRef, useEffect } from 'react';
import { LocationItem, FilterAnchorEl, ModalView } from '../../../types/search';
import { DEFAULT_PRICE_VALUES, DEFAULT_SEARCH_VALUES } from '../constants/searchConstants';
import { LocationSegment } from '../../../types/locationSegment';

export const useSearchBarState = (t: (key: string) => string) => {
  const [searchText, setSearchText] = useState('');
  const [transactionType, setTransactionType] = useState<string>(t(DEFAULT_SEARCH_VALUES.TRANSACTION_TYPE_KEY));
  const [propertyType, setPropertyType] = useState<string>(t('estate'));
  const [priceMin, setPriceMin] = useState(DEFAULT_PRICE_VALUES.MIN);
  const [priceMax, setPriceMax] = useState(DEFAULT_PRICE_VALUES.MAX);
  const [minInputValue, setMinInputValue] = useState(DEFAULT_PRICE_VALUES.MIN_INPUT);
  const [maxInputValue, setMaxInputValue] = useState(DEFAULT_PRICE_VALUES.MAX_INPUT);
  const [priceErrorMessage, setPriceErrorMessage] = useState<string>('');

  const [filterAnchorEl, setFilterAnchorEl] = useState<FilterAnchorEl>(null);
  const [locationAnchorEl, setLocationAnchorEl] = useState<FilterAnchorEl>(null);
  const [selectedBedrooms, setSelectedBedrooms] = useState<string | null>(null);
  const [selectedBathrooms, setSelectedBathrooms] = useState<string | null>(null);
  const [selectedQuota, setSelectedQuota] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedFloor, setSelectedFloor] = useState<string | null>(null);
  const [selectedPublishedBy, setSelectedPublishedBy] = useState<string | null>(null);
  const [selectedPostedDate, setSelectedPostedDate] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<string | null>(null);
  const [selectedDirection, setSelectedDirection] = useState<string | null>(null);
  
  const [selectedDynamicFilters, setSelectedDynamicFilters] = useState<Record<string, any>>({});
  
  const [areaMin, setAreaMin] = useState(0);
  const [areaMax, setAreaMax] = useState(1000);
  const [areaMinInput, setAreaMinInput] = useState('0');
  const [areaMaxInput, setAreaMaxInput] = useState('1,000');

  const [openModal, setOpenModal] = useState(false);
  const [locationTypeId, setLocationTypeId] = useState<number | null>(null);
  const [locationData, setLocationData] = useState<LocationItem[]>([]);
  const [loadingLocationData, setLoadingLocationData] = useState(false);
  const [modalSearchValue, setModalSearchValue] = useState('');

  const [selectedLocation, setSelectedLocation] = useState<LocationItem | null>(null);
  const [selectedSubLocation, setSelectedSubLocation] = useState<LocationItem | null>(null);
  const [locationSearchText, setLocationSearchText] = useState('');

  const [locationTypes, setLocationTypes] = useState<LocationItem[]>([]);

  const [modalView, setModalView] = useState<ModalView>('main');
  const [selectedMainOption, setSelectedMainOption] = useState<LocationItem | null>(null);
  
  const [navigationHistory, setNavigationHistory] = useState<LocationItem[]>([]);

  const [isSearchDisabled, setIsSearchDisabled] = useState(false);

  const searchDebounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<LocationItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const searchInputContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [locationWithChildren, setLocationWithChildren] = useState<any[]>([]);

  const [locationSegments, setLocationSegments] = useState<LocationSegment[]>([]);

  const filterButtonRef = useRef(null);

  useEffect(() => {
    setTransactionType(t(DEFAULT_SEARCH_VALUES.TRANSACTION_TYPE_KEY));
  }, [t]);

  // segment helpers
  const pushSegment = (segment: LocationSegment) =>
    setLocationSegments(prev => [...prev, segment]);

  const popSegment = () =>
    setLocationSegments(prev => (prev.length > 0 ? prev.slice(0, -1) : prev));

  const replaceLastSegmentLabel = (label: string, locale: string = 'th') => {
    setLocationSegments(prev => {
      if (prev.length === 0) return prev;
      const last = { ...prev[prev.length - 1], label: { ...prev[prev.length - 1].label, [locale]: label } };
      return [...prev.slice(0, -1), last];
    });
  };

  return {
    searchText,
    setSearchText,
    transactionType,
    setTransactionType,
    propertyType,
    setPropertyType,
    priceMin,
    setPriceMin,
    priceMax,
    setPriceMax,
    minInputValue,
    setMinInputValue,
    maxInputValue,
    setMaxInputValue,
    priceErrorMessage,
    setPriceErrorMessage,

    filterAnchorEl,
    setFilterAnchorEl,
    locationAnchorEl,
    setLocationAnchorEl,
    selectedBedrooms,
    setSelectedBedrooms,
    selectedBathrooms,
    setSelectedBathrooms,
    selectedQuota,
    setSelectedQuota,
    selectedStatus,
    setSelectedStatus,
    selectedFloor,
    setSelectedFloor,
    selectedPublishedBy,
    setSelectedPublishedBy,
    selectedPostedDate,
    setSelectedPostedDate,
    selectedView,
    setSelectedView,
    selectedDirection,
    setSelectedDirection,
    
    selectedDynamicFilters,
    setSelectedDynamicFilters,
    
    areaMin,
    setAreaMin,
    areaMax,
    setAreaMax,
    areaMinInput,
    setAreaMinInput,
    areaMaxInput,
    setAreaMaxInput,

    openModal,
    setOpenModal,
    locationTypeId,
    setLocationTypeId,
    locationData,
    setLocationData,
    loadingLocationData,
    setLoadingLocationData,
    modalSearchValue,
    setModalSearchValue,

    selectedLocation,
    setSelectedLocation,
    selectedSubLocation,
    setSelectedSubLocation,
    locationSearchText,
    setLocationSearchText,
    locationTypes,
    setLocationTypes,

    modalView,
    setModalView,
    selectedMainOption,
    setSelectedMainOption,
    navigationHistory,
    setNavigationHistory,
    isSearchDisabled,
    setIsSearchDisabled,

    searchDebounceTimeout,
    page,
    setPage,
    hasMore,
    setHasMore,
    isLoadingMore,
    setIsLoadingMore,
    scrollRef,

    isSearching,
    setIsSearching,
    searchResults,
    setSearchResults,
    showDropdown,
    setShowDropdown,
    showLocationDropdown,
    setShowLocationDropdown,
    searchInputContainerRef,
    searchInputRef,
    locationWithChildren,
    setLocationWithChildren,

    locationSegments,
    setLocationSegments,
    pushSegment,
    popSegment,
    replaceLastSegmentLabel,

    filterButtonRef
  };
}; 